import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import {
	AnonymizedTalent,
	MatchedTalentsResponse
} from "../models/AnonymizedTalent";
import { ClientTalentMatchingAdminRow } from "../models/ClientTalentMatchingAdmin";
import {
	CreateTalentSelectionRequest,
	CreateTalentSelectionResponse,
	TalentSelectionDetail,
	TalentSelectionSummary
} from "../models/TalentSelection";

/**
 * Client API for the "Fiche Talent anonymisée" feature. Mirrors
 * AnonymizedTalentsController on the backend.
 *
 * Public endpoints (used by the prospect's browser via the token URL) do NOT
 * send credentials. We keep them in this file too so the API surface is
 * discoverable in one place.
 */

// ------------------------------------------------------------
// Option A — Client area (Mes talents suggérés)
// ------------------------------------------------------------

export const getMyMatchedTalents = async (): Promise<AnonymizedTalent[]> => {
	try {
		const response = await apiClient.get<MatchedTalentsResponse>(
			"/anonymized-talents/clients/me/matched"
		);
		return response.data.talents ?? [];
	} catch (error) {
		throw handleApiError(error, "Failed to load matched talents");
	}
};

// ------------------------------------------------------------
// Option B — CSM area (saved selections + per-talent public links)
// ------------------------------------------------------------

export const createTalentSelection = async (
	request: CreateTalentSelectionRequest
): Promise<CreateTalentSelectionResponse> => {
	try {
		const response = await apiClient.post<CreateTalentSelectionResponse>(
			"/anonymized-talents/selections",
			request
		);
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to save talent selection");
	}
};

export const listMyTalentSelections = async (): Promise<TalentSelectionSummary[]> => {
	try {
		const response = await apiClient.get<TalentSelectionSummary[]>(
			"/anonymized-talents/selections"
		);
		return response.data ?? [];
	} catch (error) {
		throw handleApiError(error, "Failed to load talent selections");
	}
};

export const getTalentSelectionDetail = async (
	selectionId: number
): Promise<TalentSelectionDetail> => {
	try {
		const response = await apiClient.get<TalentSelectionDetail>(
			`/anonymized-talents/selections/${selectionId}`
		);
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to load talent selection detail");
	}
};

export const revokeTalentPublicLink = async (
	selectionId: number,
	linkId: number
): Promise<void> => {
	try {
		await apiClient.delete(
			`/anonymized-talents/selections/${selectionId}/links/${linkId}`
		);
	} catch (error) {
		throw handleApiError(error, "Failed to revoke link");
	}
};

export const deleteTalentSelection = async (selectionId: number): Promise<void> => {
	try {
		await apiClient.delete(`/anonymized-talents/selections/${selectionId}`);
	} catch (error) {
		throw handleApiError(error, "Failed to delete selection");
	}
};

// ------------------------------------------------------------
// Internal admin audit — read-only view of every client × talent matching
// ------------------------------------------------------------

export const listClientTalentMatchingsAdmin = async (): Promise<
	ClientTalentMatchingAdminRow[]
> => {
	try {
		const response = await apiClient.get<ClientTalentMatchingAdminRow[]>(
			"/anonymized-talents/admin/client-matchings"
		);
		return response.data ?? [];
	} catch (error) {
		throw handleApiError(error, "Failed to load client/talent matchings");
	}
};

/**
 * Per-client scoped variant — rendered inline on the client detail page so the
 * CSM can verify the daily matcher's output without leaving the file.
 * Returns 0 or 1 row depending on whether the worker has produced matchings yet.
 */
export const listClientTalentMatchingsAdminForClient = async (
	clientUserId: number
): Promise<ClientTalentMatchingAdminRow[]> => {
	try {
		const response = await apiClient.get<ClientTalentMatchingAdminRow[]>(
			`/anonymized-talents/admin/clients/${clientUserId}/matchings`
		);
		return response.data ?? [];
	} catch (error) {
		throw handleApiError(error, "Failed to load client matchings");
	}
};

/**
 * CSM / Admin / TAM action: removes one talent from a client's live matched
 * batch. The matching is demoted (not deleted) server-side, so the talent
 * leaves the client's view at once and is never re-proposed by the matcher.
 */
export const excludeClientTalentMatching = async (
	matchingId: number
): Promise<void> => {
	try {
		await apiClient.delete(
			`/anonymized-talents/admin/client-matchings/${matchingId}`
		);
	} catch (error) {
		throw handleApiError(error, "Failed to exclude talent from matching");
	}
};

/**
 * CSM / Admin / TAM action: manually adds one talent to a client's live matched
 * batch (the inverse of exclude). Inserts the matching, or re-activates a
 * previously excluded one. Ephemeral — replaced by the next monthly run.
 */
export const assignClientTalentMatching = async (
	clientUserId: number,
	freelancerProfileId: number
): Promise<void> => {
	try {
		await apiClient.post(
			`/anonymized-talents/admin/clients/${clientUserId}/matchings/${freelancerProfileId}`
		);
	} catch (error) {
		throw handleApiError(error, "Failed to assign talent to client");
	}
};

/**
 * "Preview as the client sees it" — internal users only. Returns the exact
 * same anonymized DTO shape that the connected client would render in-app,
 * served from a dedicated admin endpoint (no token required).
 */
export const getAnonymizedTalentPreviewAdmin = async (
	freelancerProfileId: number,
	lang?: "fr" | "en"
): Promise<AnonymizedTalent> => {
	try {
		const response = await apiClient.get<AnonymizedTalent>(
			`/anonymized-talents/admin/preview/${freelancerProfileId}`,
			{ params: lang ? { lang } : undefined }
		);
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to load anonymized preview");
	}
};

// ------------------------------------------------------------
// Public surface — accessed without authentication
// ------------------------------------------------------------

/**
 * Tagged error thrown when the rate limiter on the public endpoint kicks in.
 * The page renders a dedicated "trop de requêtes" panel instead of the
 * generic "link unavailable" panel so the visitor knows they can simply retry.
 */
export class PublicAnonymizedTalentRateLimitedError extends Error {
	constructor() {
		super("Rate limit exceeded on the public anonymized-talent endpoint.");
		this.name = "PublicAnonymizedTalentRateLimitedError";
	}
}

const PUBLIC_FETCH_TIMEOUT_MS = 30_000;

/**
 * Resolves a public token into an anonymized talent.
 * Calls the API directly with `fetch` to avoid the axios interceptor that
 * attaches the JWT — this endpoint must stay anonymous.
 *
 * Uses `REACT_APP_API_URL` (already declared in every .env.*) so this works
 * out of the box without extra config.
 *
 * The optional `lang` param is forwarded to the backend so translatable fields
 * (Title, Presentation, JobTitle, MissionDescription, …) are served in the
 * visitor's language directly from the DB.
 *
 * Behaviour:
 *  - 404 (unknown / revoked / expired token) → resolves to `null`
 *  - 429 (rate limit) → throws <c>PublicAnonymizedTalentRateLimitedError</c>
 *  - 30s timeout via <c>AbortController</c> so the browser doesn't hang
 *    indefinitely if the backend stalls.
 */
export const getPublicAnonymizedTalent = async (
	token: string,
	lang?: "fr" | "en"
): Promise<AnonymizedTalent | null> => {
	const baseUrl = (process.env.REACT_APP_API_URL ?? "").replace(/\/$/, "");
	const langQuery = lang ? `?lang=${lang}` : "";
	const url = `${baseUrl}/anonymized-talents/public/${encodeURIComponent(token)}${langQuery}`;

	const controller = new AbortController();
	const timeoutId = window.setTimeout(
		() => controller.abort(),
		PUBLIC_FETCH_TIMEOUT_MS
	);

	let response: Response;
	try {
		response = await fetch(url, {
			method: "GET",
			headers: { Accept: "application/json" },
			signal: controller.signal
		});
	} finally {
		window.clearTimeout(timeoutId);
	}

	if (response.status === 404) {
		return null;
	}

	if (response.status === 429) {
		throw new PublicAnonymizedTalentRateLimitedError();
	}

	if (!response.ok) {
		throw new Error(`Failed to load public talent (${response.status})`);
	}

	return (await response.json()) as AnonymizedTalent;
};
